import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Award, Upload, Loader2 } from "lucide-react";

export function ChampionSpotlightForm() {
  const [c, setC] = useState({ champion_name: "", champion_role: "", champion_photo_url: null, registered: null });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/care-home/champion").then((r) => setC({ ...r.data, champion_name: r.data.champion_name || "", champion_role: r.data.champion_role || "" })).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put("/care-home/champion", { champion_name: c.champion_name, champion_role: c.champion_role }); toast.success("Oral Champion saved"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/care-home/champion-photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setC((x) => ({ ...x, champion_photo_url: data.champion_photo_url })); toast.success("Photo uploaded"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <form onSubmit={save} data-testid="champion-form" className="space-y-5">
      <div className="flex items-center gap-2 text-brand"><Award className="h-5 w-5 text-coral" /><h3 className="font-heading font-bold">Oral Champion spotlight</h3></div>
      <p className="text-sm text-muted-foreground">Shown on your printed Team poster so residents and families know who leads mouth care.{c.registered && !c.champion_name ? ` We've pre-filled ${c.registered.name} from your Oral Champion registration.` : ""}</p>
      <div className="flex flex-wrap items-center gap-5">
        <div data-testid="champion-photo-preview" className="h-24 w-24 rounded-full border border-cream-deep bg-white overflow-hidden grid place-items-center shrink-0">
          {c.champion_photo_url ? <img src={c.champion_photo_url} alt="Oral Champion" className="h-full w-full object-cover" /> : <Award className="h-8 w-8 text-brand/30" />}
        </div>
        <div>
          <input ref={fileRef} data-testid="champion-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
          <Button type="button" size="sm" variant="outline" data-testid="champion-photo-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="rounded-full h-8">
            {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}{c.champion_photo_url ? "Replace photo" : "Upload photo"}
          </Button>
          <p className="text-xs text-muted-foreground mt-1">Square head-and-shoulders photo works best.</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>Champion's name</Label><Input data-testid="champion-name" value={c.champion_name} onChange={(e) => setC({ ...c, champion_name: e.target.value })} placeholder={c.registered?.name || "e.g. Sarah Jones"} /></div>
        <div><Label>Role</Label><Input data-testid="champion-role" value={c.champion_role} onChange={(e) => setC({ ...c, champion_role: e.target.value })} placeholder="e.g. Senior Care Assistant · Oral Champion" /></div>
      </div>
      <Button type="submit" data-testid="champion-save-btn" disabled={saving} variant="outline" className="rounded-full border-brand text-brand h-10 px-6">{saving ? "Saving…" : "Save champion"}</Button>
    </form>
  );
}
